/* eslint-disable */
import React, { useState, useCallback } from 'react';
import {
  Accordion, AccordionSummary, AccordionDetails,
  Box, Typography, TextField, Grid,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import InfoIcon from '@mui/icons-material/Info';
import EditItemView from './components/EditItemView';
import EditAttributesAccordion from './components/EditAttributesAccordion';
import SettingsMenu from './components/SettingsMenu';
import UploadAvatar from '../common/components/upload/UploadAvatar';
import palette from '../common/theme/palette';
import { useTranslation } from '../common/components/LocalizationProvider';
import { formatDataByte } from '../common/util/formatter';
import { useCatch } from '../reactHelper';
import { useAdministrator } from '../common/util/permissions';

const useStyles = makeStyles((theme) => ({
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(3),
  },
}));

const RevendaPage = () => {
  const classes = useStyles();
  const t = useTranslation();
  const admin = useAdministrator();

  const [item, setItem] = useState();

  // Prevê o logo; no preview pós-upload, usa o blob; em recarga, cai no endpoint do backend
  const getLogo = (rev) => {
        if (rev.logoPreview) {
          return { preview: rev.logoPreview };
        }
        return `/api/revendas/logo/${rev.code}`;
      };

  // Handle upload de logo: envia arquivo cru e atualiza estado
  const handleLogoDrop = useCatch(async (acceptedFiles) => {
    if (!item?.id || acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    const response = await fetch(`/api/revendas/${item.id}/logo`, {
      method: 'POST',
      headers: {
        'Content-Type': file.type,    // assegura que o backend receba o tipo correto
      },
      body: file,
    });
    if (!response.ok) {
      throw new Error(await response.text());
    }
    const filename = await response.text();
    const previewUrl = URL.createObjectURL(file);
    setItem({ ...item, logo: filename, logoPreview: previewUrl });
  });

  const validate = () => item && item.code && item.title && item.url;

  return (
    <EditItemView
      endpoint="revendas"
      item={item}
      setItem={setItem}
      validate={validate}
      menu={<SettingsMenu />}
      breadcrumbs={['userAdmin', 'sharedRevendas', 'sharedRevenda']}
    >
      {item && (
        <>
          {/* Obrigatórios */}
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                {t('sharedRequired') || 'Campos Obrigatórios'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Box sx={{ mb: 12 }}>
                    <UploadAvatar
                      accept="image/*"                                // qualquer tipo de arquivo
                      file={getLogo(item)}
                      maxSize={Number.MAX_SAFE_INTEGER}           // sem limite prático
                      onDrop={handleLogoDrop}
                      caption={t('attributeResaleLogo') || 'Logo da Revenda'}
                    />
                    <Typography
                      variant="caption"
                      style={{ display: 'block', textAlign: 'center', color: 'text.secondary' }}
                    >
                      {t('sharedAllowed') || 'Qualquer tipo de arquivo permitido'}<br />
                      {t('sharedMaxSize') || 'Sem limite de tamanho prático'}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={8}>
                  {admin && (
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        margin="normal"
                        value={item.code || ''}
                        onChange={(e) => setItem({ ...item, code: e.target.value })}
                        label={t('sharedCodeInternal') || 'Código Interno'}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={10}>
                    <TextField
                      fullWidth
                      margin="normal"
                      value={item.title || ''}
                      onChange={(e) => setItem({ ...item, title: e.target.value })}
                      label={t('sharedName') || 'Nome'}
                    />
                  </Grid>

                  <Grid item xs={12} sm={10}>
                    <TextField
                      fullWidth
                      margin="normal"
                      value={item.url || ''}
                      onChange={(e) => setItem({ ...item, url: e.target.value })}
                      label={t('sharedUrl') || 'URL'}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                {t('sharedInfo') || 'Informações'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    margin="normal"
                    value={item.template || ''}
                    onChange={(event) => setItem({ ...item, template: event.target.value })}
                    label={t('sharedName') || 'Nome'}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    margin="normal"
                    value={item.phone || ''}
                    onChange={(event) => setItem({ ...item, phone: event.target.value })}
                    label={t('sharedPhone') || 'Telefone'}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    margin="normal"
                    value={item.email || ''}
                    onChange={(event) => setItem({ ...item, email: event.target.value })}
                    label={t('sharedEmail') || 'Email'}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} sm={2}>
                  <TextField
                    margin="normal"
                    value={item.document || ''}
                    onChange={(event) => setItem({ ...item, document: event.target.value })}
                    label={t('sharedDocument') || 'Documento'}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="normal"
                    value={item.address || ''}
                    onChange={(event) => setItem({ ...item, address: event.target.value })}
                    label={t('sharedAddress') || 'Endereço'}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="normal"
                    value={item.neighborhood || ''}
                    onChange={(event) => setItem({ ...item, neighborhood: event.target.value })}
                    label={t('sharedNeighborhood') || 'Bairro'}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="normal"
                    value={item.city || ''}
                    onChange={(event) => setItem({ ...item, city: event.target.value })}
                    label={t('sharedCity') || 'Cidade'}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    margin="normal"
                    value={item.state || ''}
                    onChange={(event) => setItem({ ...item, state: event.target.value })}
                    label={t('sharedState') || 'Estado'}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    margin="normal"
                    value={item.postalcode || ''}
                    onChange={(event) => setItem({ ...item, postalcode: event.target.value })}
                    label={t('sharedPostalCode') || 'CEP'}
                    fullWidth
                  />
                </Grid>
{/*                 <Grid item xs={12} sm={2}> */}
{/*                   <TextField */}
{/*                     margin="normal" */}
{/*                     value={item.creationTime || ''} */}
{/*                     onChange={(event) => setItem({ ...item, duedate: event.target.value })} */}
{/*                     label={t('sharedDuedate') || 'Data de Vencimento'} */}
{/*                     fullWidth */}
{/*                   /> */}
{/*                 </Grid> */}
              </Grid>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                {t('sharedPreferences') || 'Preferências'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    margin="normal"
                    value={item.whatsapp || ''}
                    onChange={(event) => setItem({ ...item, whatsapp: event.target.value })}
                    label={t('sharedWhatsapp') || 'WhatsApp'}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    margin="normal"
                    value={item.twitter || ''}
                    onChange={(event) => setItem({ ...item, twitter: event.target.value })}
                    label={t('sharedTwitter') || 'Twitter'}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    margin="normal"
                    value={item.instagram || ''}
                    onChange={(event) => setItem({ ...item, instagram: event.target.value })}
                    label={t('sharedInstagram') || 'Instagram'}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="normal"
                    value={item.facebook || ''}
                    onChange={(event) => setItem({ ...item, facebook: event.target.value })}
                    label={t('sharedFacebook') || 'Facebook'}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="normal"
                    value={item.googlekey || ''}
                    onChange={(event) => setItem({ ...item, googlekey: event.target.value })}
                    label={t('sharedGoogleKey') || 'Chave do Google'}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                {t('sharedRevendaCustom') || 'Configurações Personalizadas'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={2}>
                  <TextField
                    margin="normal"
                    value={item.color1 || 'red'}
                    onChange={(event) => setItem({ ...item, color1: event.target.value })}
                    label={t('sharedColor1') || 'Cor 1'}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    margin="normal"
                    value={item.color2 || 'red'}
                    onChange={(event) => setItem({ ...item, color2: event.target.value })}
                    label={t('sharedColor2') || 'Cor 2'}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    margin="normal"
                    value={item.color3 || '#cccccc'}
                    onChange={(event) => setItem({ ...item, color3: event.target.value })}
                    label={t('sharedColor3') || 'Cor 3'}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    margin="normal"
                    value={item.color4 || 'red'}
                    onChange={(event) => setItem({ ...item, color4: event.target.value })}
                    label={t('sharedColor4') || 'Cor 4'}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    margin="normal"
                    value={item.color5 || '#000'}
                    onChange={(event) => setItem({ ...item, color5: event.target.value })}
                    label={t('sharedColor5') || 'Cor 5'}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                {t('sharedApps') || 'Aplicativos'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="normal"
                    value={item.googleplay || ''}
                    onChange={(event) => setItem({ ...item, googleplay: event.target.value })}
                    label={t('sharedGooglePlay') || 'Google Play'}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="normal"
                    value={item.applestore || ''}
                    onChange={(event) => setItem({ ...item, applestore: event.target.value })}
                    label={t('sharedAppleStore') || 'Apple Store'}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          <EditAttributesAccordion
            attributes={item.attributes}
            setAttributes={(attributes) => setItem({ ...item, attributes })}
            definitions={{}}
          />
        </>
      )}
    </EditItemView>
  );
};

export default RevendaPage;